/** Parse YYYY-MM-DD as a local calendar date (no UTC shift). */
export function parseIsoCalendarDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10))
  return new Date(y, (m || 1) - 1, d || 1)
}

/** Format a Date as YYYY-MM-DD using local calendar components. */
export function formatCalendarDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/** First calendar day users may schedule (day after businessToday). */
export function minimumSelectableScheduleDate(businessTodayIso: string): Date {
  const d = parseIsoCalendarDate(businessTodayIso)
  d.setDate(d.getDate() + 1)
  return d
}

/** True when iso is strictly after businessToday and not in lockedDates. */
export function isScheduleDateSelectable(
  iso: string,
  businessTodayIso: string,
  lockedDates: ReadonlySet<string> | readonly string[],
): boolean {
  if (iso <= businessTodayIso) return false
  const locked = lockedDates instanceof Set ? lockedDates : new Set(lockedDates)
  return !locked.has(iso)
}

const ISO_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

/** True for a bare YYYY-MM-DD string (no time component). */
export function isIsoCalendarDate(value: unknown): value is string {
  return typeof value === "string" && ISO_CALENDAR_DATE.test(value)
}

/**
 * Earliest start date (YYYY-MM-DD) a campaign may use: the stricter of the day after
 * businessToday (lockout rule) and the station order rule's minimumStartDate (lead time).
 * Both come from the backend in the same business timezone, so plain string compare is safe.
 */
export function earliestScheduleStartIso(
  businessTodayIso: string,
  orderRuleMinimumIso?: string | null,
): string {
  const dayAfterToday = formatCalendarDate(minimumSelectableScheduleDate(businessTodayIso))
  if (isIsoCalendarDate(orderRuleMinimumIso) && orderRuleMinimumIso > dayAfterToday) {
    return orderRuleMinimumIso
  }
  return dayAfterToday
}

/**
 * Inclusive YYYY-MM-DD range covering exactly `days` local calendar days ending on `today`
 * (billing APIs treat date_from/date_to as inclusive, so "last 7 days" is today-6 … today).
 */
export function lastNDaysRange(days: number, today: Date = new Date()): { from: string; to: string } {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const start = new Date(end)
  start.setDate(end.getDate() - (Math.max(1, Math.floor(days)) - 1))
  return { from: formatCalendarDate(start), to: formatCalendarDate(end) }
}
